/**
 * URL-first property context hook.
 *
 * Reads propertyId from React Router URL params when present (/properties/:propertyId/*),
 * falls back to activePropertyId from useAuth() for backward compatibility during migration.
 *
 * Architectural rule: Property context must be resolvable from URL + Auth Token alone.
 * Session-only property state exists only for backward compatibility and must not be
 * required for correctness.
 */

import { useParams, useNavigate } from "react-router"
import { useAuth } from "@/lib/auth"
import { useMemo, useCallback, useRef } from "react"

interface Property {
  id: string
  name: string
  pms_provider?: string
  address?: string
  timezone?: string
  cover_image_url?: string
}

interface PropertyContextValue {
  /** The resolved property ID (from URL or auth fallback) */
  propertyId: string
  /** Full property metadata from the user's property list */
  property: Property | null
  /** Whether auth data is still loading */
  isLoading: boolean
  /** Where the property ID was resolved from */
  source: "url" | "auth" | "none"
  /** Whether the user has access to this property */
  hasAccess: boolean
  /** Whether this is a demo property (pms_provider === "demo") */
  isDemo: boolean
  /**
   * Monotonic version counter. Increments whenever propertyId changes.
   * Downstream hooks should use this to safely reset state and avoid stale data.
   */
  contextVersion: number
  /** Navigate to a section within this property workspace */
  navigateToSection: (section: string) => void
}

/**
 * Monotonic version counter per property context change.
 * Lives outside React to survive re-renders and avoid stale closure issues.
 */
let _contextVersion = 0
let _lastPropertyId = ""

export function usePropertyContext(): PropertyContextValue {
  const params = useParams<{ propertyId?: string }>()
  const auth = useAuth()
  const navigate = useNavigate()

  const urlPropertyId = params.propertyId?.trim() || ""
  const authPropertyId = auth.activePropertyId || ""

  // URL always takes precedence when present
  const effectiveId = urlPropertyId || authPropertyId
  const source: "url" | "auth" | "none" = urlPropertyId
    ? "url"
    : authPropertyId
      ? "auth"
      : "none"

  // Bump version when propertyId changes
  if (effectiveId !== _lastPropertyId) {
    _contextVersion++
    _lastPropertyId = effectiveId
  }

  // Check access against user's property list
  const hasAccess = useMemo(() => {
    if (!effectiveId || auth.isLoading) return true // optimistic while loading
    return auth.properties.some((p) => p.id === effectiveId)
  }, [effectiveId, auth.properties, auth.isLoading])

  const property = useMemo(() => {
    if (!hasAccess || !effectiveId) return null
    return (auth.properties.find((p) => p.id === effectiveId) as Property) || null
  }, [auth.properties, effectiveId, hasAccess])

  const isDemo = property?.pms_provider === "demo"

  const navigateToSection = useCallback(
    (section: string) => {
      if (effectiveId) {
        navigate(`/properties/${effectiveId}/${section}`)
      }
    },
    [navigate, effectiveId],
  )

  return {
    propertyId: hasAccess ? effectiveId : "",
    property,
    isLoading: auth.isLoading,
    source,
    hasAccess,
    isDemo,
    contextVersion: _contextVersion,
    navigateToSection,
  }
}
